import { z } from "zod";
import { MASTER_DATA_ENTITY_TYPES } from "@/lib/enums";

/**
 * Zod schemas for `master_data_mapping` (resolves a Coach Manager master-data
 * reference from its source-BU value to the equivalent target-BU value).
 *
 * `sourceBuId`/`targetBuId` must differ — a mapping is always between two
 * distinct BUs. The base schema stays un-refined so `.partial()` composes
 * cleanly for the update schema; each of the create/update schemas attaches
 * its own `.refine()` afterward.
 */

const masterDataMappingBaseSchema = z.object({
  entityType: z.enum(MASTER_DATA_ENTITY_TYPES),
  sourceBuId: z.string().min(1, "Source BU is required"),
  targetBuId: z.string().min(1, "Target BU is required"),
  sourceRefId: z.string().min(1, "Source reference ID is required").max(200, "Too long"),
  targetRefId: z.string().min(1, "Target reference ID is required").max(200, "Too long"),
  isActive: z.boolean(),
});

// Form schema — every field always present (this entity has no "leave blank
// to keep existing" field like bu_instance's password), so create and the
// form share one shape.
export const masterDataMappingFormSchema = masterDataMappingBaseSchema.refine(
  (data) => data.sourceBuId !== data.targetBuId,
  { message: "Source and target BU must be different", path: ["targetBuId"] },
);

export type MasterDataMappingFormValues = z.infer<typeof masterDataMappingFormSchema>;

// POST reuses the form schema directly — unlike bu_instance, every field is
// always required here, so there's no separate create-vs-form shape.

// PUT is a partial update — only re-check the BU-pair constraint when both
// fields are present in the same patch; a patch touching only sourceRefId
// shouldn't need to re-validate a BU pair it isn't changing.
export const updateMasterDataMappingSchema = masterDataMappingBaseSchema
  .partial()
  .refine(
    (data) =>
      data.sourceBuId === undefined || data.targetBuId === undefined
        ? true
        : data.sourceBuId !== data.targetBuId,
    { message: "Source and target BU must be different", path: ["targetBuId"] },
  );

export type CreateMasterDataMappingInput = MasterDataMappingFormValues;
export type UpdateMasterDataMappingInput = z.infer<typeof updateMasterDataMappingSchema>;

/** Body for `POST /api/admin/master-data-mappings/sync-drafts` — see `syncMasterDataMappingDrafts`. */
export const syncMasterDataMappingDraftsSchema = z.object({
  sourceBuId: z.string().min(1, "Source BU is required"),
  targetBuId: z.string().min(1, "Target BU is required"),
  entityType: z.enum(MASTER_DATA_ENTITY_TYPES),
});
